import BpkLink from '@skyscanner/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import RatingStatement from '../RatingStatement';

import type { TranslationKey } from '@shared/constants/translations';
import type { Configs } from '@shared/types/configs';
import type { HotelReviewSummary } from '@shared/types/hotel-content';
import type { I18nService } from '@shared/types/i18n';
import type { UserContext } from '@shared/types/user-context';

import STYLES from './HotelRating.module.scss';

import SectionHeader from '@hotels-hotel-detail/ui/SectionHeader';
import { HASH_REVIEWS } from '@shared/constants/hash-routes';
import BpkBreakpoint, { BREAKPOINTS } from '@shared/ui/common/BpkBreakpoint';
import RouteLink from '@shared/ui/common/RouteLink';
import ReviewRating, {
  SPACING_VARIANTS,
} from '@shared/ui/components/ReviewRating';
import { FormattedMessage } from '@shared/ui/components/i18n';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withConfiguration } from '@shared/utils-frontend/contexts/ConfigurationContext';
import { withUserContext } from '@shared/utils-frontend/contexts/UserContext';

const cls = cssModules(STYLES);

type Props = {
  i18n: I18nService;
  showRatingTitle?: boolean;
  configs: Configs;
  userContext: UserContext;
  reviewSummary: HotelReviewSummary;
};

const HotelRating = ({
  configs: { enableRatingsReviewsSEOVariant },
  i18n,
  reviewSummary,
  showRatingTitle = true,
  userContext: { enableSEOVariantHotelsDetailsPage },
}: Props) => {
  const title: TranslationKey =
    enableSEOVariantHotelsDetailsPage && enableRatingsReviewsSEOVariant
      ? 'OnPageNavigation_HDP_ratings_and_reviews'
      : 'Hotel_review_title';

  return (
    <div data-test-id="reviews-summary-ratings">
      <BpkBreakpoint query={BREAKPOINTS.MOBILE}>
        {(isMobile: boolean) => (
          <>
            {showRatingTitle && (
              <SectionHeader
                title={<FormattedMessage label={title} />}
                textStyle={
                  isMobile ? TEXT_STYLES.heading4 : TEXT_STYLES.heading1
                }
                mainClassName={cls('HotelRating__sectionHeader')}
                className={cls('HotelRating__sectionHeaderTitle')}
                viewMoreButton={
                  isMobile ? (
                    <RouteLink
                      href={HASH_REVIEWS}
                      render={(onClick, linkHref) => (
                        <BpkLink href={linkHref ?? null} onClick={onClick}>
                          <BpkText textStyle={TEXT_STYLES.label1}>
                            {i18n.translate('Button_viewMore')}
                          </BpkText>
                        </BpkLink>
                      )}
                    />
                  ) : null
                }
              />
            )}
            <div className={cls('HotelRating')}>
              {isMobile ? (
                <div className={cls('HotelRating__ratingAndStatement')}>
                  <ReviewRating
                    score={reviewSummary.score}
                    count={reviewSummary.reviewCount}
                    image={reviewSummary.scoreImageUrl}
                    scoreDescription={reviewSummary.scoreDescription}
                    large
                  />
                  <RatingStatement
                    ratingProvider={reviewSummary.ratingProvider}
                  />
                </div>
              ) : (
                <ReviewRating
                  score={reviewSummary.score}
                  count={reviewSummary.reviewCount}
                  image={reviewSummary.scoreImageUrl}
                  scoreDescription={reviewSummary.scoreDescription}
                  spacingVariant={SPACING_VARIANTS.FIXED_RATIO}
                  large
                />
              )}
            </div>
          </>
        )}
      </BpkBreakpoint>
    </div>
  );
};

export default withUserContext(withConfiguration(withI18n(HotelRating)));
